import { isMobile } from "@/common/browserDetect";
import { addBuyFlowLogEntry } from "@/common/buyflowTracker";
import { pushGoogleTagManageEvent } from "@/components/BuyEnhancedCart/BuyBaseHelper";
import {
  config,
  ICartLayoutConfig,
  IComponentConfig,
  layoutItemNames,
  sortLayoutByColumnAndPosition,
  stepOrder,
} from "@/components/BuyEnhancedCart/DynamicComponents/Config";
import { buyFlowPageOneRoute } from "@/define";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

export const useBuyFlowManagerStore = defineStore("buyFlowManagerStore", () => {
  const route = useRoute();
  const sku = ref<string>("");
  const ratePlanId = ref<string>("");
  const prospectGuid = ref<string>("");
  const orderCode = ref<string>("");
  const step = ref<string>(""); //Remove if old cart is discontinued
  const deviceId = ref<number>(0);
  const userGuid = ref<string>("");
  const subscriptionMonths = ref<number>(12);
  const quantity = ref<number>(1);
  const userId = ref<number>(0);
  const promoCode = ref<string>("");
  const catId = ref<number>(0);
  const subscriptionNumber = ref<string>("");
  const includedUnits = ref<number>(1);
  const emailAc = ref<number | null>(null);
  const emailRc = ref<number | null>(null);
  const emailEmcid = ref<number | null>(null);
  const emailUtmContent = ref<string | null>(null);
  const emailUtmMedium = ref<string | null>(null);
  const emailUtmSource = ref<string | null>(null);
  const emailUtmTerm = ref<string | null>(null);
  const emailUtmCampaign = ref<string | null>(null);
  const enrollmentGuid = ref<string>("");
  const licenseKey = ref<string>("");
  const vendorKey = ref<string>("");
  const loadCartFromDB = ref<boolean>(false);
  const loadSessionStorageCart = ref<boolean>(false);
  const isWebrootPurchase = ref<boolean>(false);

  const showShippingAddressForm = ref<boolean>(true);
  const shippingFormHasErrors = ref<boolean>(false);
  const paymentFormHasErrors = ref<boolean>(false);
  const iFrameHasErrors = ref<boolean>(false);
  const emailHasErrors = ref<boolean>(false);
  const hasAllStateError = ref<boolean>(false);
  const isAddingCard = ref<boolean>(false);

  const layoutConfig = ref<ICartLayoutConfig>(config);
  const previousStep = ref<IComponentConfig | null>(); //Private: Should not be directly modified
  const currentStep = ref<IComponentConfig | null>(); //Private: Should not be directly modified
  const buyflowSteps = ref<IComponentConfig[]>(stepOrder);
  const completedSteps = ref<string[]>([]);

  const editStep = ref<IComponentConfig | null>(); //Private: Should not be directly modified

  const isStepInEdit = computed(() => {
    return !!editStep.value;
  });

  const openStep = computed(() => {
    return currentStep.value ?? buyflowSteps.value[0];
  });

  const lastStep = computed(() => {
    return previousStep.value ?? buyflowSteps.value[0];
  });

  const nextStep = computed(() => {
    if (currentStep.value?.stepOrder && !isFinalStep.value) {
      return buyflowSteps.value[currentStep.value.stepOrder];
    }

    //This shouldn't happen, but set it to the current step as a fall back
    return currentStep.value ?? buyflowSteps.value[0];
  });

  const layout = computed(() => {
    //const items = layoutConfig.value.mobile;
    const items = isMobile() ? layoutConfig.value.mobile : layoutConfig.value.desktop;

    return sortLayoutByColumnAndPosition(items);
  });

  const allStepsCompleted = computed(() => {
    return completedSteps.value.length === buyflowSteps.value.length;
  });

  const isFinalStep = computed(() => {
    return buyflowSteps.value[buyflowSteps.value.length - 1].name === openStep.value.name;
  });

  const allComponentsErrorFree = computed(() => {
    return (
      !shippingFormHasErrors.value &&
      !iFrameHasErrors.value &&
      !emailHasErrors.value &&
      !hasAllStateError.value &&
      !paymentFormHasErrors.value
    );
  });

  //Repurposed for GTM to know what buyflow the user is in
  const isPageOne = computed(() => {
    return route.path.toLowerCase() === buyFlowPageOneRoute;
  });

  const allowCartItemEdit = computed(() => {
    return openStep.value.name !== layoutItemNames.reviewOrder;
  });

  function setStartingStepState() {
    if (!currentStep.value) {
      currentStep.value = buyflowSteps.value[0];
    }
  }

  function completeStep(step: string) {
    if (!completedSteps.value.some(l => l === step)) {
      completedSteps.value.push(step);
    }
  }

  function clearCompletedStep(step: string) {
    if (completedSteps.value.some(l => l === step)) {
      completedSteps.value = completedSteps.value.filter(l => l !== step);
    }
  }

  function isStepComplete(step: string) {
    return completedSteps.value.some(l => l === step);
  }

  //Figure out the next step and go to it
  function goToNextStep(isCancel = false) {
    if (!currentStep.value) {
      return;
    }

    if (!isCancel) {
      completeStep(currentStep.value.name);
    }

    //Clear the edit flag
    if (isStepInEdit.value) {
      editStep.value = null;
    }

    //Copy of current step to set the previous step
    const step = Object.assign({}, currentStep.value);

    //Check if the user should return to the last step they were on
    if (
      lastStep.value &&
      lastStep.value.stepOrder &&
      currentStep.value.stepOrder &&
      lastStep.value.stepOrder > currentStep.value.stepOrder
    ) {
      //Create a copy so the references don't mix
      currentStep.value = Object.assign({}, lastStep.value);
    } else {
      currentStep.value = Object.assign({}, nextStep.value);
    }

    // if (!isCancel) {
    //   addBuyFlowLogEntry(`M: ${step}`);
    // }

    previousStep.value = step;
    pushGoogleTagManageEvent();
  }

  //Skip the next step and go to the one after it.
  //Should be used for skipping the shipping step
  function skipNextStep() {
    if (!currentStep.value) {
      return;
    }

    completeStep(currentStep.value.name);

    //Clear the edit flag
    if (isStepInEdit.value) {
      editStep.value = null;
    }

    //Copy of current step to set the previous step
    const step = Object.assign({}, currentStep.value);

    //Check if the user should return to the last step they were on
    if (
      lastStep.value &&
      lastStep.value.stepOrder &&
      currentStep.value.stepOrder &&
      lastStep.value.stepOrder > currentStep.value.stepOrder
    ) {
      //Create a copy so the references don't mix
      currentStep.value = Object.assign({}, lastStep.value);
    } else {
      //Set next step to complete
      completeStep(nextStep.value.name);
      //Find the step after the next step
      const stepAfterNextIndex = buyflowSteps.value.findIndex(l => l.name === nextStep.value.name);
      if (stepAfterNextIndex) {
        //Set step after next step as the current step
        currentStep.value = Object.assign({}, buyflowSteps.value[stepAfterNextIndex + 1]);
      }
    }

    previousStep.value = step;
    pushGoogleTagManageEvent();
  }

  //Go directly to a step
  async function goToStep(step: layoutItemNames) {
    if (!currentStep.value) {
      return;
    }

    previousStep.value = Object.assign({}, currentStep.value);
    currentStep.value = Object.assign(
      {},
      buyflowSteps.value.find(l => l.name === step)
    );

    pushGoogleTagManageEvent();
  }

  // Set current step (Required if we need to jump between steps)
  //completed steps is updated so that all below step orders are updated as completed
  function setCurrentStep(step: layoutItemNames) {
    if (!currentStep.value) {
      return;
    }

    const newStep = buyflowSteps.value.find(l => l.name === step);
    if (!newStep || !newStep.stepOrder) {
      return;
    }

    const newPreviousPosition = newStep?.stepOrder - 1;
    previousStep.value = buyflowSteps.value.find(l => l.stepOrder === newPreviousPosition);
    currentStep.value = Object.assign(
      {},
      buyflowSteps.value.find(l => l.name === step)
    );

    completedSteps.value = [];
    for (let i = 0; i < currentStep.value.stepOrder!; i++) {
      completedSteps.value.push(buyflowSteps.value[i].name);
    }

    pushGoogleTagManageEvent();
  }

  function setEditStep(step: layoutItemNames) {
    addBuyFlowLogEntry(`Edit button clicked: ${step}`);
    const stepToEdit = buyflowSteps.value.find(l => l.name === step);
    if (!stepToEdit) {
      return;
    }

    editStep.value = Object.assign({}, stepToEdit);
    goToStep(step);
  }

  function cancelEdit() {
    addBuyFlowLogEntry(`Cancel Edit button clicked: ${openStep.value.name}`);
    editStep.value = null;
    //Go back to where the user was
    goToNextStep(true);
  }

  return {
    sku,
    ratePlanId,
    prospectGuid,
    orderCode,
    step,
    deviceId,
    userGuid,
    subscriptionMonths,
    quantity,
    userId,
    promoCode,
    catId,
    subscriptionNumber,
    includedUnits,
    emailAc,
    emailEmcid,
    emailRc,
    emailUtmContent,
    emailUtmMedium,
    emailUtmSource,
    emailUtmTerm,
    emailUtmCampaign,
    enrollmentGuid,
    licenseKey,
    vendorKey,
    loadCartFromDB,
    loadSessionStorageCart,
    isWebrootPurchase,
    showShippingAddressForm,
    isPageOne,
    shippingFormHasErrors,
    allComponentsErrorFree,
    iFrameHasErrors,
    emailHasErrors,
    hasAllStateError,
    paymentFormHasErrors,
    layoutConfig,
    buyflowSteps,
    layout,
    openStep,
    lastStep,
    nextStep,
    completeStep,
    clearCompletedStep,
    isStepComplete,
    allStepsCompleted,
    goToNextStep,
    setStartingStepState,
    goToStep,
    setCurrentStep,
    isFinalStep,
    completedSteps,
    isStepInEdit,
    setEditStep,
    cancelEdit,
    allowCartItemEdit,
    skipNextStep,
    isAddingCard,
  };
});
